import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { firestoreService } from '../services/firestore.service';
import { cacheService } from '../services/cache.service';
import { createDebouncedSync, initializeFromFirestore, SYNC_TIMING } from '../services/syncHelper';

// Default user profile state
const defaultUserProfile = {
    name: '',
    location: {
        country: '',
        city: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        coordinates: null,
    },
    language: navigator.language?.split('-')[0] || 'en',
    ageRange: '',
    aboutYou: '',
    hobbies: [],
    interests: {
        health: false,
        finance: false,
        home: false,
        career: false,
        family: false,
        travel: false,
        fitness: false,
        cooking: false,
        learning: false,
        socializing: false,
        creativity: false,
        pets: false,
        gardening: false,
        technology: false,
        wellness: false,
        sustainability: false,
    },
    selectedTheme: 'grownups',
    techSavvyLevel: 'standard',
    selectedGrownupsTasks: [],
    selectedActivities: [],
    selectedCalendars: [],
};

const defaultOnboardingState = {
    isOnboardingComplete: false,
    hasSeenWelcome: false,
    currentStep: 0,
    totalSteps: 13,
};

export const useOnboardingStore = create(
    persist(
        (set, get) => ({
            userProfile: { ...defaultUserProfile },
            onboarding: { ...defaultOnboardingState },
            userId: null,
            isInitialized: false,

            /**
             * Initialize profile from Firestore
             */
            initializeWithUser: async (userId) => {
                set({ userId });

                await initializeFromFirestore({
                    getCached: () => cacheService.getUser()?.profile,
                    getFromFirestore: async () => {
                        const profile = await firestoreService.getUserProfile(userId);
                        const onboardingData = await firestoreService.getOnboardingChoices(userId);
                        return { profile, onboardingData };
                    },
                    onData: (data) => {
                        if (data?.profile || data?.onboardingData) {
                            set({
                                userProfile: {
                                    ...defaultUserProfile,
                                    ...data.profile,
                                    ...(data.onboardingData?.userProfile || {})
                                },
                                onboarding: data.onboardingData ? {
                                    isOnboardingComplete: data.onboardingData.isOnboardingComplete || false,
                                    hasSeenWelcome: data.onboardingData.hasSeenWelcome || false,
                                    currentStep: data.onboardingData.currentStep ?? 0,
                                    totalSteps: get().onboarding.totalSteps,
                                } : get().onboarding,
                            });
                        }
                        set({ isInitialized: true });
                    },
                    onError: () => set({ isInitialized: true }),
                });
            },

            /**
             * Sync profile to Firestore (debounced)
             */
            syncProfile: () => {
                const { userId, userProfile, onboarding } = get();
                if (!userId) return;

                createDebouncedSync(
                    'profile',
                    async () => {
                        await firestoreService.updateUserProfile(userId, userProfile);
                        await firestoreService.saveOnboardingChoices(userId, {
                            ...onboarding,
                            userProfile,
                        });
                    },
                    () => cacheService.updateUser({ profile: userProfile }),
                    SYNC_TIMING.NORMAL
                )();
            },

            clearUserData: () => {
                set({ userId: null, isInitialized: false });
            },

            // ===== PROFILE ACTIONS =====

            setName: (name) => {
                set((state) => ({ userProfile: { ...state.userProfile, name } }));
                get().syncProfile();
            },

            setLocation: (location) => {
                set((state) => ({
                    userProfile: {
                        ...state.userProfile,
                        location: { ...state.userProfile.location, ...location }
                    }
                }));
                get().syncProfile();
            },

            setLanguage: (language) => {
                set((state) => ({ userProfile: { ...state.userProfile, language } }));
                get().syncProfile();
            },

            setAgeRange: (ageRange) => {
                set((state) => ({ userProfile: { ...state.userProfile, ageRange } }));
                get().syncProfile();
            },

            setAboutYou: (aboutYou) => {
                set((state) => ({ userProfile: { ...state.userProfile, aboutYou } }));
                get().syncProfile();
            },

            setHobbies: (hobbies) => {
                set((state) => ({ userProfile: { ...state.userProfile, hobbies } }));
                get().syncProfile();
            },

            addHobby: (hobby) => {
                set((state) => ({
                    userProfile: {
                        ...state.userProfile,
                        hobbies: state.userProfile.hobbies.includes(hobby)
                            ? state.userProfile.hobbies
                            : [...state.userProfile.hobbies, hobby]
                    }
                }));
                get().syncProfile();
            },

            removeHobby: (hobby) => {
                set((state) => ({
                    userProfile: {
                        ...state.userProfile,
                        hobbies: state.userProfile.hobbies.filter(h => h !== hobby)
                    }
                }));
                get().syncProfile();
            },

            toggleInterest: (interest) => {
                set((state) => ({
                    userProfile: {
                        ...state.userProfile,
                        interests: {
                            ...state.userProfile.interests,
                            [interest]: !state.userProfile.interests[interest]
                        }
                    }
                }));
                get().syncProfile();
            },

            setSelectedTheme: (selectedTheme) => {
                set((state) => ({ userProfile: { ...state.userProfile, selectedTheme } }));
                get().syncProfile();
            },

            setTechSavvyLevel: (techSavvyLevel) => {
                set((state) => ({ userProfile: { ...state.userProfile, techSavvyLevel } }));
                get().syncProfile();
            },

            toggleGrownupsTask: (task) => {
                set((state) => {
                    const exists = state.userProfile.selectedGrownupsTasks.some(t => t.id === task.id);
                    return {
                        userProfile: {
                            ...state.userProfile,
                            selectedGrownupsTasks: exists
                                ? state.userProfile.selectedGrownupsTasks.filter(t => t.id !== task.id)
                                : [...state.userProfile.selectedGrownupsTasks, task]
                        }
                    };
                });
                get().syncProfile();
            },

            setSelectedGrownupsTasks: (tasks) => {
                set((state) => ({ userProfile: { ...state.userProfile, selectedGrownupsTasks: tasks } }));
                get().syncProfile();
            },

            toggleActivity: (activity) => {
                set((state) => {
                    const exists = state.userProfile.selectedActivities.some(a => a.name === activity.name);
                    return {
                        userProfile: {
                            ...state.userProfile,
                            selectedActivities: exists
                                ? state.userProfile.selectedActivities.filter(a => a.name !== activity.name)
                                : [...state.userProfile.selectedActivities, activity]
                        }
                    };
                });
                get().syncProfile();
            },

            setSelectedActivities: (selectedActivities) => {
                set((state) => ({ userProfile: { ...state.userProfile, selectedActivities } }));
                get().syncProfile();
            },

            setSelectedCalendars: (selectedCalendars) => {
                set((state) => ({ userProfile: { ...state.userProfile, selectedCalendars } }));
                get().syncProfile();
            },

            toggleCalendar: (calendar) => {
                set((state) => {
                    const exists = state.userProfile.selectedCalendars.some(c => c.id === calendar.id);
                    return {
                        userProfile: {
                            ...state.userProfile,
                            selectedCalendars: exists
                                ? state.userProfile.selectedCalendars.filter(c => c.id !== calendar.id)
                                : [...state.userProfile.selectedCalendars, calendar]
                        }
                    };
                });
                get().syncProfile();
            },

            // ===== ONBOARDING FLOW ACTIONS =====

            nextStep: () => {
                set((state) => ({
                    onboarding: {
                        ...state.onboarding,
                        currentStep: Math.min(state.onboarding.currentStep + 1, state.onboarding.totalSteps - 1),
                    }
                }));
                get().syncProfile();
            },

            previousStep: () => {
                set((state) => ({
                    onboarding: {
                        ...state.onboarding,
                        currentStep: Math.max(state.onboarding.currentStep - 1, 0)
                    }
                }));
                get().syncProfile();
            },

            goToStep: (step) => {
                set((state) => ({
                    onboarding: {
                        ...state.onboarding,
                        currentStep: Math.max(0, Math.min(step, state.onboarding.totalSteps - 1))
                    }
                }));
                get().syncProfile();
            },

            markWelcomeSeen: () => {
                set((state) => ({
                    onboarding: { ...state.onboarding, hasSeenWelcome: true }
                }));
                get().syncProfile();
            },

            completeOnboarding: () => {
                set((state) => ({
                    onboarding: { ...state.onboarding, isOnboardingComplete: true }
                }));
                get().syncProfile();
            },

            // ===== UTILITY ACTIONS =====

            resetOnboarding: () => set({
                userProfile: { ...defaultUserProfile },
                onboarding: { ...defaultOnboardingState }
            }),

            getSelectedInterests: () => {
                const { interests } = get().userProfile;
                return Object.keys(interests).filter(key => interests[key]);
            },

            isProfileValid: () => {
                const { name } = get().userProfile;
                return name.trim().length > 0;
            },
        }),
        {
            name: 'grownups-onboarding-storage',
            partialize: (state) => ({
                userProfile: state.userProfile,
                onboarding: {
                    isOnboardingComplete: state.onboarding.isOnboardingComplete,
                    hasSeenWelcome: state.onboarding.hasSeenWelcome,
                    currentStep: state.onboarding.currentStep,
                    totalSteps: state.onboarding.totalSteps,
                },
            }),
            merge: (persistedState, currentState) => ({
                ...currentState,
                ...persistedState,
                onboarding: {
                    ...currentState.onboarding,
                    ...persistedState?.onboarding,
                },
                userProfile: {
                    ...currentState.userProfile,
                    ...persistedState?.userProfile,
                    location: {
                        ...currentState.userProfile.location,
                        ...persistedState?.userProfile?.location,
                    },
                    interests: {
                        ...currentState.userProfile.interests,
                        ...persistedState?.userProfile?.interests,
                    },
                },
            }),
        }
    )
);
